// Q9 S3 — Final review step (D24 — free back-nav + final review pattern).
// Summarizes each completed section with key fields and an edit pencil
// that navigates the wizard back to that step. Submit affordance lives
// in the WizardStepFooter, not here, so the reading order stays
// summary → submit.

import type { JSX } from "react";
import type { FormState } from "../lib/visit-report-types.js";
import type { SectionName } from "../api/visit-report-api.js";

interface SectionSummary {
  id: SectionName;
  label: string;
  bullets: string[];
}

function summariseVitals(f: FormState): string[] {
  const rows: Array<[boolean, string]> = [
    [Boolean(f.bp_sys || f.bp_dia), `BP ${f.bp_sys || "?"}/${f.bp_dia || "?"} mmHg`],
    [Boolean(f.hr),                  `HR ${f.hr} bpm`],
    [Boolean(f.spo2),                `SpO₂ ${f.spo2}%`],
    [Boolean(f.temp),                `Temp ${f.temp}°C`],
    [Boolean(f.glucose),             `Glucose ${f.glucose} mmol/L`],
    [Boolean(f.weight),              `Weight ${f.weight} kg`],
    [Boolean(f.rr),                  `RR ${f.rr}/min`],
    [Boolean(f.ankle_oedema),        `Ankle oedema: ${f.ankle_oedema}`],
  ];
  return rows.filter(([on]) => on).map(([, line]) => line);
}

function summariseMobility(f: FormState): string[] {
  const out: string[] = [];
  if (f.mobility) out.push(`Mobility: ${f.mobility}`);
  if (f.fall_visit === "yes") out.push("Fall during visit");
  if (f.fall_since === "yes") out.push(`Falls since last visit: ${f.fall_count_since_last_visit || "?"}`);
  if (f.escalation_severity) out.push(`Escalation severity: ${f.escalation_severity}`);
  return out;
}

function summariseClinical(f: FormState): string[] {
  const out: string[] = [];
  if (f.overall_status) out.push(`Status: ${f.overall_status.replace(/_/g, " ")}`);
  if (f.pain_present === "yes") out.push(`Pain ${f.pain_severity || "?"}/10`);
  if (f.cognitive) out.push(`Cognition: ${f.cognitive}`);
  if (f.mood) out.push(`Mood: ${f.mood}`);
  return out;
}

const SYMPTOM_LABELS: Array<[keyof FormState, string]> = [
  ["fever_or_night_sweats",          "fever"],
  ["persistent_cough",               "cough"],
  ["dizziness_reported",             "dizziness"],
  ["nausea_or_vomiting",             "nausea"],
  ["unusual_fatigue_reported",       "fatigue"],
  ["unplanned_weight_loss_reported", "weight loss"],
];

function summariseWellbeing(f: FormState): string[] {
  const symptoms = SYMPTOM_LABELS.filter(([k]) => f[k] === "yes").map(([, l]) => l);
  const rows: Array<[boolean, string]> = [
    [f.breathlessness_present === "yes", `Breathlessness (${f.breathlessness_severity || "?"})`],
    [Boolean(f.eating_status),           `Eating: ${f.eating_status}`],
    [Boolean(f.sleep_quality),           `Sleep: ${f.sleep_quality}`],
    [symptoms.length > 0,                `Symptoms: ${symptoms.join(", ")}`],
  ];
  return rows.filter(([on]) => on).map(([, line]) => line);
}

function summariseMedication(f: FormState): string[] {
  const meds = f.meds.filter((m) => m.drug_name.trim());
  if (meds.length === 0) return ["No medications logged"];
  return meds.map((m) => `${m.drug_name} — ${m.adherence_status.replace(/_/g, " ")}`);
}

function summariseNotes(f: FormState): string[] {
  const out: string[] = [];
  if (f.notes) out.push(`Notes (${f.notes.length} chars)`);
  if (f.recommendations.length) out.push(`Recommendations: ${f.recommendations.length}`);
  return out;
}

const SECTION_LABELS: Record<SectionName, string> = {
  vitals:     "Vitals",
  mobility:   "Mobility",
  clinical:   "Clinical",
  wellbeing:  "Wellbeing",
  medication: "Medication",
  notes:      "Notes",
};

function buildSummaries(f: FormState): SectionSummary[] {
  return [
    { id: "vitals",     label: SECTION_LABELS.vitals,     bullets: summariseVitals(f) },
    { id: "mobility",   label: SECTION_LABELS.mobility,   bullets: summariseMobility(f) },
    { id: "clinical",   label: SECTION_LABELS.clinical,   bullets: summariseClinical(f) },
    { id: "wellbeing",  label: SECTION_LABELS.wellbeing,  bullets: summariseWellbeing(f) },
    { id: "medication", label: SECTION_LABELS.medication, bullets: summariseMedication(f) },
    { id: "notes",      label: SECTION_LABELS.notes,      bullets: summariseNotes(f) },
  ];
}

interface Props {
  formState: FormState;
  completedSteps: ReadonlySet<string>;
  incompleteSteps?: ReadonlySet<string>;
  onEdit: (sectionId: SectionName) => void;
}

export function ReviewStep({ formState, completedSteps, incompleteSteps = new Set(), onEdit }: Props): JSX.Element {
  const summaries = buildSummaries(formState);

  return (
    <div className="space-y-3" data-testid="review-step">
      <h2 className="text-base font-semibold text-slate-900">Review before submitting</h2>
      {summaries.map((s) => {
        const status = incompleteSteps.has(s.id) ? "incomplete"
                     : completedSteps.has(s.id)  ? "complete"
                     : "future";
        return (
          <article key={s.id} data-testid={`review-${s.id}`} data-status={status}
            className="bg-white border border-slate-200 rounded-lg p-4 flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-medium text-slate-900">{s.label}</h3>
                {status === "complete"   && <span className="text-xs text-green-700">✓ complete</span>}
                {status === "incomplete" && <span className="text-xs text-amber-700">⚠ incomplete</span>}
                {status === "future"     && <span className="text-xs text-slate-400">— not yet entered</span>}
              </div>
              {s.bullets.length > 0
                ? <ul className="text-sm text-slate-600 space-y-0.5">
                    {s.bullets.map((b, i) => <li key={i}>{b}</li>)}
                  </ul>
                : <p className="text-sm text-slate-400 italic">No data captured.</p>}
            </div>
            <button
              type="button"
              onClick={() => onEdit(s.id)}
              aria-label={`Edit ${s.label}`}
              className="text-sm text-navy-700 hover:underline px-2 py-1"
            >
              Edit
            </button>
          </article>
        );
      })}
    </div>
  );
}
